(() => {
if (window.__mpsSmartUpsellLoaded) return;
window.__mpsSmartUpsellLoaded = true;

/**
 * Smart upsells: rule-based accessory suggestions (Vendor + Product type + Tags).
 * <smart-upsell data-sources="/collections/all/yamaha-mt-07-2020,..." data-vendors="dna"
 *   data-types="motorcycle performance air filters" data-tags="" data-exclude="123,456"
 *   data-limit="4" data-layout="grid|row">
 */

const feedCache = new Map();
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

const list = (value) =>
  (value || '')
    .split(/[|,]/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);

const escapeHtml = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const CACHE_PREFIX = 'mps_su_';
const CACHE_TTL = 10 * 60 * 1000;

function readCache(path) {
  try {
    const c = JSON.parse(sessionStorage.getItem(CACHE_PREFIX + path) || 'null');
    if (c && Date.now() - c.t < CACHE_TTL && Array.isArray(c.d)) return c.d;
  } catch (e) {}
  return null;
}
function writeCache(path, data) {
  try {
    sessionStorage.setItem(CACHE_PREFIX + path, JSON.stringify({ t: Date.now(), d: data }));
  } catch (e) {
    // Storage full: clear older upsell feeds and carry on without caching.
    try {
      Object.keys(sessionStorage).filter((k) => k.startsWith(CACHE_PREFIX)).forEach((k) => sessionStorage.removeItem(k));
    } catch (err) {}
  }
}

function fetchFeed(path) {
  if (!feedCache.has(path)) {
    const cached = readCache(path);
    if (cached) {
      feedCache.set(path, Promise.resolve(cached));
      return feedCache.get(path);
    }
    const url = `${path}${path.includes('?') ? '&' : '?'}view=upsell-data`;
    feedCache.set(
      path,
      fetch(url, { credentials: 'same-origin', priority: 'low' })
        .then((r) => (r.ok ? r.text() : '[]'))
        .then((txt) => {
          try {
            const data = JSON.parse(txt);
            if (Array.isArray(data)) writeCache(path, data);
            return Array.isArray(data) ? data : [];
          } catch (e) {
            return [];
          }
        })
        .catch(() => [])
    );
  }
  return feedCache.get(path);
}

// Load upsell rows only when they come near the viewport (or when a hidden cart drawer opens).
const lazyObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (items) => {
          items.forEach((item) => {
            if (!item.isIntersecting) return;
            lazyObserver.unobserve(item.target);
            item.target.load();
          });
        },
        { rootMargin: '600px 0px' }
      )
    : null;

class SmartUpsell extends HTMLElement {
  connectedCallback() {
    // Inside the cart drawer, load straight away: the drawer scrolls on its own, so the
    // viewport observer may never see this row on mobile and the placeholder would keep shimmering.
    if (this.closest('cart-drawer')) return this.load();
    if (lazyObserver) lazyObserver.observe(this);
    else this.load();
  }

  disconnectedCallback() {
    if (lazyObserver) lazyObserver.unobserve(this);
  }

  get listEl() {
    return this.querySelector('[data-upsell-list]');
  }

  async load() {
    const listEl = this.listEl;
    if (!listEl || this.dataset.loading === 'true') return;
    if (listEl.dataset.rendered === 'true' && listEl.children.length) return;

    const sources = list(this.dataset.sources).map((s) => s.replace(/^\/?/, root));
    if (!sources.length) return this.hide();

    this.dataset.loading = 'true';
    const vendors = list(this.dataset.vendors);
    const types = list(this.dataset.types);
    const typeContains = list(this.dataset.typeContains);
    const excludeTypes = list(this.dataset.excludeTypes);
    const tags = list(this.dataset.tags);
    const exclude = new Set(list(this.dataset.exclude));
    const limit = parseInt(this.dataset.limit || '4', 10);
    const shownOnPage = this.dataset.dedupe === 'true' ? (window.__suShown = window.__suShown || new Set()) : new Set();

    const feeds = await Promise.all(sources.map(fetchFeed));
    const seen = new Set();
    const matches = [];

    for (const feed of feeds) {
      for (const p of feed) {
        const id = String(p.id);
        if (seen.has(id) || exclude.has(id) || shownOnPage.has(id) || !p.av) continue;
        const ty = String(p.ty).toLowerCase();
        if (vendors.length && !vendors.includes(String(p.v).toLowerCase())) continue;
        if (excludeTypes.includes(ty)) continue;
        if (types.length || typeContains.length) {
          const okType = types.includes(ty) || typeContains.some((w) => ty.includes(w));
          if (!okType) continue;
        }
        if (tags.length) {
          const ptags = list(p.tg);
          if (!tags.some((t) => ptags.includes(t))) continue;
        }
        seen.add(id);
        matches.push(p);
      }
    }

    /* Buyer logic: group by product type in the rule's priority order, then pick round-robin
       so the customer sees a mix (filter, ECU map, decat...) rather than 4 of the same thing. */
    const rank = (p) => {
      const ty = String(p.ty).toLowerCase();
      let i = types.indexOf(ty);
      if (i === -1) {
        const j = typeContains.findIndex((w) => ty.includes(w));
        i = j === -1 ? 999 : types.length + j;
      }
      return i;
    };
    const groups = new Map();
    matches
      .map((p, idx) => ({ p, r: rank(p), idx }))
      .sort((a, b) => a.r - b.r || a.idx - b.idx)
      .forEach(({ p }) => {
        const key = String(p.ty).toLowerCase();
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(p);
      });
    const queues = [...groups.values()];
    const picks = [];
    while (picks.length < limit && queues.some((q) => q.length)) {
      for (const q of queues) {
        if (picks.length >= limit) break;
        if (q.length) picks.push(q.shift());
      }
    }
    picks.forEach((p) => shownOnPage.add(String(p.id)));

    this.dataset.loading = 'false';
    if (!picks.length) return this.hide();

    listEl.innerHTML = picks.map((p) => this.card(p)).join('');
    listEl.dataset.rendered = 'true';
    this.show();
  }

  card(p) {
    const multi = p.vc > 1;
    const img = p.img
      ? `<img src="${escapeHtml(p.img)}" alt="${escapeHtml(p.t)}" loading="${this.closest('cart-drawer') ? 'eager' : 'lazy'}" width="360" height="360">`
      : '';
    const price = `<span class="su-card__price">${p.cp ? `<s>${escapeHtml(p.cp)}</s> ` : ''}${escapeHtml(p.p)}</span>`;
    const action = multi
      ? `<a class="su-card__btn su-card__btn--ghost" href="${escapeHtml(p.u)}">${escapeHtml(this.dataset.chooseLabel || 'Choose options')}</a>`
      : `<button type="button" class="su-card__btn" data-upsell-add data-variant-id="${p.vid}">${escapeHtml(this.dataset.addLabel || 'Add to cart')}</button>`;
    return `<li class="su-card">
      <a class="su-card__media" href="${escapeHtml(p.u)}" tabindex="-1" aria-hidden="true">${img}</a>
      <div class="su-card__body">
        <span class="su-card__vendor">${escapeHtml(p.v)}</span>
        <a class="su-card__title" href="${escapeHtml(p.u)}">${escapeHtml(p.t)}</a>
        ${price}
      </div>
      ${action}
    </li>`;
  }

  hide() {
    this.dataset.loading = 'false';
    const wrap = this.closest('[data-upsell-wrap]') || this;
    wrap.hidden = true;
  }

  show() {
    const wrap = this.closest('[data-upsell-wrap]') || this;
    wrap.hidden = false;
  }
}

if (!customElements.get('smart-upsell')) customElements.define('smart-upsell', SmartUpsell);

/* ---------- Add to cart (Enterprise: refresh the cart drawer + cart icon, then fire on:cart:add) ---------- */

async function addToCart(button) {
  const variantId = button.dataset.variantId;
  if (!variantId || button.disabled) return;
  const original = button.textContent;
  button.disabled = true;
  button.classList.add('is-loading');

  const cartDrawer = document.querySelector('cart-drawer');
  const sections = ['cart-icon-bubble'];
  if (cartDrawer && cartDrawer.closest('.shopify-section')) {
    sections.push(cartDrawer.closest('.shopify-section').id.replace('shopify-section-', ''));
  }

  const formData = new FormData();
  formData.append('id', String(variantId));
  formData.append('quantity', '1');
  formData.append('sections', sections.join(','));
  formData.append('sections_url', window.location.pathname);

  try {
    const res = await fetch((window.theme && window.theme.routes && window.theme.routes.cartAdd) || `${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    let data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) {
      button.textContent = data.description || data.message || 'Unavailable';
      return;
    }

    // Cart upsell deal: apply its discount code, keeping any codes already on the cart.
    const dealCode = upsellDealCode(button);
    if (dealCode) {
      const updated = await applyDealCode(dealCode, sections);
      if (updated && updated.sections) data = updated;
    }

    if (data.sections) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && data.sections['cart-icon-bubble']) {
        const tpl = document.createElement('template');
        tpl.innerHTML = data.sections['cart-icon-bubble'];
        const updated = tpl.content.querySelector('.shopify-section');
        bubble.innerHTML = updated ? updated.innerHTML : data.sections['cart-icon-bubble'];
      }
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(data, button.closest('cart-drawer') === null);
      }
    }

    document.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { variantId, source: 'smart-upsell' } }));
    button.textContent = button.dataset.addedLabel || 'Added ✓';
    button.classList.add('is-added');
  } catch (e) {
    button.textContent = original;
    button.disabled = false;
  } finally {
    button.classList.remove('is-loading');
  }
}

function upsellDealCode(el) {
  const wrap = el && el.closest && el.closest('[data-upsell-wrap][data-discount-code]');
  return wrap ? wrap.dataset.discountCode.trim() : '';
}

async function applyDealCode(code, sections) {
  try {
    const cart = await fetch(`${root}cart.js`, { credentials: 'same-origin' }).then((r) => r.json());
    const codes = (cart.discount_codes || []).map((d) => d.code).filter(Boolean);
    if (codes.some((c) => c.toLowerCase() === code.toLowerCase())) return null;
    codes.push(code);
    const body = { discount: codes.join(',') };
    if (sections && sections.length) {
      body.sections = sections.join(',');
      body.sections_url = window.location.pathname;
    }
    const res = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    });
    return res.ok ? res.json() : null;
  } catch (e) {
    return null;
  }
}

// "Choose options" on a deal card: put the code on the cart before leaving for the product page.
document.addEventListener('click', (e) => {
  const link = e.target.closest && e.target.closest('[data-upsell-wrap][data-discount-code] .su-card__btn--ghost');
  if (!link) return;
  applyDealCode(upsellDealCode(link));
});

document.addEventListener('click', (e) => {
  const btn = e.target.closest && e.target.closest('[data-upsell-add]');
  if (!btn) return;
  e.preventDefault();
  addToCart(btn);
});

/* Cart drawer re-renders can wipe injected cards: re-run after every cart change. */
['on:cart:add', 'on:line-item:change', 'dispatch:cart-drawer:refresh'].forEach((name) => {
  document.addEventListener(name, () => {
    setTimeout(() => {
      document.querySelectorAll('smart-upsell').forEach((el) => el.load && el.load());
    }, 300);
  });
});
})();
